import { useState } from 'react';

import { useDebounce } from '@/shared/hooks/use-debounce/use-debounce';

export const useSearchableSelect = <TOptionShape>(
  onSelectChange?: (
    selectedOptions: TOptionShape[],
    value: TOptionShape | TOptionShape[],
    setValue: (value: TOptionShape | TOptionShape[]) => void,
  ) => void,
) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [shouldSearch, setShouldSearch] = useState(true);
  const debouncedSearch = useDebounce({
    value: shouldSearch ? inputValue : '',
  });

  const handleInputChange = (
    _: unknown,
    newInputValue: string,
    reason: string,
  ) => {
    // `inputValue` is passed back to MUI Autocomplete as a controlled prop
    // (see base-searchable-select.tsx), so it must stay in sync for every
    // reason (typing, selecting an option, clearing) or the field would
    // freeze on stale text. Only genuine typing ('input') should trigger a
    // new debounced search.
    setInputValue(newInputValue);
    if (reason === 'input') {
      setShouldSearch(true);
    }
  };

  const handleSelectChange = (
    selectedOptions: TOptionShape[],
    value: TOptionShape | TOptionShape[],
    setValue: (value: TOptionShape | TOptionShape[]) => void,
  ) => {
    setShouldSearch(false);
    if (onSelectChange) {
      onSelectChange(selectedOptions, value, setValue);
    }
  };

  return {
    inputValue,
    shouldSearch,
    debouncedSearch,
    handleSelectChange,
    handleInputChange,
  };
};
